import React from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Clock, User, CheckCircle2, Monitor, Search } from "lucide-react";

/**
 * ITTicketStats — Tarjetas de estadísticas y barra de filtros de Tickets Internos IT.
 *
 * Extraído 1:1 desde `pages/ITTicketsPage.js`. Muestra 4 contadores (Nuevos, En
 * Tratamiento, Resueltos, Total Abiertos) y la barra de filtros: buscador libre + filtros
 * por tipo, estado y usuario asignado.
 *
 * @param {Object}   props.stats            Totales ({ nueva, en_tratamiento, resuelto, total_open }).
 * @param {string}   props.searchTerm       Término de búsqueda actual.
 * @param {Function} props.setSearchTerm    Setter del término de búsqueda.
 * @param {string}   props.typeFilter       Filtro por tipo ("all" | INCIDENCIA | SOLICITUD).
 * @param {Function} props.setTypeFilter    Setter del filtro por tipo.
 * @param {string}   props.statusFilter     Filtro por estado ("all" | estado).
 * @param {Function} props.setStatusFilter  Setter del filtro por estado.
 * @param {string}   props.assignedFilter   Filtro por asignado ("all" | "unassigned" | userId).
 * @param {Function} props.setAssignedFilter Setter del filtro por asignado.
 * @param {Object}   props.statusLabels     Mapa estado→etiqueta (para el desplegable de estado).
 * @param {Array}    props.supportUsers     Usuarios asignables (para el desplegable de asignado).
 */
export const ITTicketStats = ({
  stats,
  searchTerm,
  setSearchTerm,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  assignedFilter,
  setAssignedFilter,
  statusLabels,
  supportUsers,
}) => {
  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Clock className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Nuevos</p>
                <p className="text-2xl font-bold text-white">{stats.nueva}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <User className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">En Tratamiento</p>
                <p className="text-2xl font-bold text-white">{stats.en_tratamiento}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Resueltos</p>
                <p className="text-2xl font-bold text-white">{stats.resuelto}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A35] border-[#1E1E5F]">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#03BEE4]/20 rounded-lg">
                <Monitor className="h-5 w-5 text-[#03BEE4]" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Abiertos</p>
                <p className="text-2xl font-bold text-white">{stats.total_open}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por título, número o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-[180px] bg-[#0A0A35] border-[#1E1E5F] text-white">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                <SelectItem value="all" className="text-white">Todos los tipos</SelectItem>
                <SelectItem value="INCIDENCIA" className="text-white">Incidencias</SelectItem>
                <SelectItem value="SOLICITUD" className="text-white">Solicitudes</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px] bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="it-filter-status">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                <SelectItem value="all" className="text-white">Todos los estados</SelectItem>
                {Object.entries(statusLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={assignedFilter} onValueChange={setAssignedFilter}>
              <SelectTrigger className="w-full md:w-[200px] bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="it-filter-assigned">
                <SelectValue placeholder="Asignado a" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                <SelectItem value="all" className="text-white">Todos los asignados</SelectItem>
                <SelectItem value="unassigned" className="text-slate-400">Sin asignar</SelectItem>
                {supportUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id} className="text-white">{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
